/**
 * gemscape-classify.js
 *
 * ── INTERMEDIARY MODULE ─────────────────────────────────────────────
 * This module classifies raw primitive-generated SVG shapes into two
 * visual tiers (base / top) by measuring their x-extent and
 * bucketing by relative width.
 *
 * This classification is an interim solution.  In a future branch the
 * gemscape generation pipeline will be updated to emit pre-classified,
 * pre-tagged SVGs directly — each shape will already carry its
 * `data-layer`, CSS class, and custom properties.  At that point this
 * entire module can be deleted and the import removed from
 * gemscape-cache.js with no further changes to the cache or rendering
 * pipeline.
 *
 * What the server-side pipeline will provide:
 *   - `data-layer="base|top"` on each shape element
 *   - `.gemscape-shape .layer-{base|top}` CSS classes
 *   - `--shape-color` / `--shape-opacity` CSS custom properties
 *   - DOM order: widest → narrowest (for correct SVG paint order)
 *
 * So anything below is purely a client-side workaround until that
 * pipeline ships.
 * ─────────────────────────────────────────────────────────────────────
 */

// ─── Palette & fast nearest-colour lookup ───────────────────────────
// Pre-computed RGB triples — parsed once at module load.
const PALETTE_HEX = [
  "#AA64AC",
  "#9269AF",
  "#6E70B0",
  "#6A85B1",
  "#6896AC",
  "#65B3A1"
];

const PALETTE_RGB = PALETTE_HEX.map(h => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16)
]);

/**
 * Return the nearest palette hex for an arbitrary CSS hex colour.
 * Uses squared-Euclidean distance in sRGB — no sqrt, no allocation.
 *
 * Accepts 3-digit (#abc) or 6-digit (#aabbcc) hex, with or without
 * leading '#'. Falls back to first palette entry for unparseable input.
 */
function quantizeColor(hex) {
  if (!hex || hex.charAt(0) !== "#") return PALETTE_HEX[0];

  let r, g, b;
  if (hex.length === 4) {
    // #abc → #aabbcc
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }

  // eslint-disable-next-line no-self-compare
  if (r !== r) return PALETTE_HEX[0]; // NaN guard

  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < PALETTE_RGB.length; i++) {
    const pr = PALETTE_RGB[i][0];
    const pg = PALETTE_RGB[i][1];
    const pb = PALETTE_RGB[i][2];
    const d = (r - pr) * (r - pr) + (g - pg) * (g - pg) + (b - pb) * (b - pb);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return PALETTE_HEX[bestIdx];
}

// ─── Polygon → Path conversion (enables CSS `d` transitions) ────────
function convertPolygonToPath(poly) {
  const pts = (poly.getAttribute("points") || "").trim();
  if (!pts) return poly;
  const coords = pts.split(/[\s,]+/);
  if (coords.length < 4) return poly;
  let d = `M ${coords[0]} ${coords[1]}`;
  for (let i = 2; i < coords.length - 1; i += 2) {
    d += ` L ${coords[i]} ${coords[i + 1]}`;
  }
  d += " Z";
  const path = poly.ownerDocument.createElementNS(
    "http://www.w3.org/2000/svg",
    "path"
  );
  path.setAttribute("d", d);
  for (const { name, value } of poly.attributes) {
    if (name !== "points") path.setAttribute(name, value);
  }
  poly.replaceWith(path);
  return path;
}

// ─── Shape Classification + Tagging ─────────────────────────────────
// Classify shapes into base/top by width, then stamp data attributes.
// Polygons are converted to paths up-front for CSS `d` transitions.
// Only needs relative widths in local coordinate space.

export function classifyAndTagShapes(svgEl) {
  // Convert polygons → paths so CSS can animate the d property on hover
  svgEl.querySelectorAll("polygon").forEach(poly => convertPolygonToPath(poly));

  const shapeEls = svgEl.querySelectorAll("path, circle, rect, ellipse");
  if (!shapeEls.length) return;

  const shapes = [];
  shapeEls.forEach((el, index) => {
    const fill = el.getAttribute("fill");
    if (fill === "none" || fill === "transparent") return;

    const xr = shapeXRange(el);
    shapes.push({
      el,
      index,
      xMin: xr.xMin,
      xMax: xr.xMax,
      width: xr.xMax - xr.xMin,
      fill: fill || "#000000",
      opacity: parseFloat(
        el.getAttribute("opacity") || el.getAttribute("fill-opacity") || "1"
      ),
      layer: null
    });
  });

  if (!shapes.length) return;

  // Base = any shape whose width spans ≥ 1/3 of the content x-range
  const allXMin = Math.min(...shapes.map(s => s.xMin));
  const allXMax = Math.max(...shapes.map(s => s.xMax));
  const coordSpan = allXMax - allXMin || 1;
  const baseThreshold = coordSpan / 3;

  shapes.forEach(s => {
    s.layer = s.width >= baseThreshold ? "base" : "top";
  });

  // Stamp data attributes + CSS classes, quantize fill to palette
  shapes.forEach(({ el, index, layer, fill, opacity }) => {
    const qColor = quantizeColor(fill);
    el.setAttribute("data-shape-id", index);
    el.setAttribute("data-layer", layer);
    el.setAttribute("fill", qColor);
    el.classList.add("gemscape-shape", `layer-${layer}`);
    el.style.setProperty("--shape-color", qColor);
    el.style.setProperty("--shape-opacity", opacity);
  });

  // Re-order DOM nodes: widest → narrowest (SVG paints later = on top).
  // Primitive generates large shapes first; this puts small shards on top.
  const sorted = [...shapes].sort((a, b) => b.width - a.width);
  sorted.forEach(({ el }) => {
    el.parentNode.appendChild(el);
  });
}

// ─── Shape X-Range ──────────────────────────────────────────────────
// Returns { xMin, xMax } from a shape's coordinate attributes.
// Only needs local-space values for relative width comparison.

function shapeXRange(el) {
  const tag = el.tagName.toLowerCase();

  if (tag === "rect") {
    const x = parseFloat(el.getAttribute("x") || 0);
    const w = parseFloat(el.getAttribute("width") || 0);
    return { xMin: x, xMax: x + w };
  }
  if (tag === "circle") {
    const cx = parseFloat(el.getAttribute("cx") || 0);
    const r = parseFloat(el.getAttribute("r") || 0);
    return { xMin: cx - r, xMax: cx + r };
  }
  if (tag === "ellipse") {
    const cx = parseFloat(el.getAttribute("cx") || 0);
    const rx = parseFloat(el.getAttribute("rx") || 0);
    return { xMin: cx - rx, xMax: cx + rx };
  }
  if (tag === "path") {
    // Rough: pull all numbers, take every-other as x candidates
    const nums = (el.getAttribute("d") || "").match(/-?\d+\.?\d*/g);
    if (!nums) return { xMin: 0, xMax: 0 };
    let xMin = Infinity,
      xMax = -Infinity;
    for (let i = 0; i < nums.length; i += 2) {
      const x = Number(nums[i]);
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
    }
    return isFinite(xMin) ? { xMin, xMax } : { xMin: 0, xMax: 0 };
  }

  return { xMin: 0, xMax: 0 };
}
